/**
 * Incremental Server-Sent Events parser.
 *
 * Written against the WHATWG event-stream rules (not `EventSource`, which
 * cannot issue a POST nor carry auth headers): chunks arrive out of a
 * `fetch` ReadableStream in arbitrary sizes, so a field can be split across
 * two network reads. The parser keeps a buffer and only emits an event when a
 * blank line closes the record — this is what guarantees we never render half
 * a JSON payload.
 *
 * Ordering is a property of the transport, not of this parser: a single HTTP
 * response is an ordered byte stream, so events come out in the exact order
 * the server wrote them.
 */

const BOM = '﻿';

/**
 * @typedef {Object} SSEEvent
 * @property {string} event — event name (`message` when the server omits it)
 * @property {string} data — concatenated `data:` lines
 * @property {string|null} id — last `id:` seen for this event
 * @property {number|null} retry — `retry:` in ms, when present
 */

/**
 * @param {(event: SSEEvent) => void} onEvent
 * @returns {{ push: (chunk: string) => void, flush: () => void, reset: () => void }}
 */
export function createSSEParser(onEvent) {
  let buffer = '';
  let eventName = '';
  let dataLines = [];
  let lastId = null;
  let retry = null;
  let hasData = false;

  const resetRecord = () => {
    eventName = '';
    dataLines = [];
    retry = null;
    hasData = false;
  };

  const dispatch = () => {
    // A record with no data and no name is just a heartbeat separator.
    if (!hasData && !eventName) {
      resetRecord();
      return;
    }
    onEvent({
      event: eventName || 'message',
      data: dataLines.join('\n'),
      id: lastId,
      retry,
    });
    resetRecord();
  };

  const handleLine = line => {
    if (line === '') {
      dispatch();
      return;
    }
    // Comment line — servers use `: keep-alive` to hold the socket open.
    if (line.startsWith(':')) return;

    const colonAt = line.indexOf(':');
    const field = colonAt === -1 ? line : line.slice(0, colonAt);
    let value = colonAt === -1 ? '' : line.slice(colonAt + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'event':
        eventName = value;
        break;
      case 'data':
        dataLines.push(value);
        hasData = true;
        break;
      case 'id':
        // NUL is not allowed in an id per spec; ignore instead of storing it.
        if (!value.includes(' ')) lastId = value;
        break;
      case 'retry': {
        const parsed = Number.parseInt(value, 10);
        if (Number.isInteger(parsed) && parsed >= 0) retry = parsed;
        break;
      }
      default:
        break;
    }
  };

  return {
    push(chunk) {
      if (!chunk) return;
      buffer += chunk.replace(/\r\n|\r/g, '\n');
      if (buffer.startsWith(BOM)) buffer = buffer.slice(1);

      let newlineAt = buffer.indexOf('\n');
      while (newlineAt !== -1) {
        const line = buffer.slice(0, newlineAt);
        buffer = buffer.slice(newlineAt + 1);
        handleLine(line);
        newlineAt = buffer.indexOf('\n');
      }
    },
    /**
     * Flushes a trailing line that never got its newline (server closed the
     * connection right after writing the last record).
     */
    flush() {
      if (buffer) {
        const line = buffer;
        buffer = '';
        handleLine(line);
      }
      dispatch();
    },
    reset() {
      buffer = '';
      lastId = null;
      resetRecord();
    },
  };
}

/**
 * Safe JSON parse for `data:` payloads. A malformed frame must never take the
 * whole stream down — the caller decides what to do with `null`.
 * @param {string} raw
 * @returns {Object|null}
 */
export function parseEventData(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
